import classNames from 'classnames';
import { isArray, random } from 'lodash';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import {
  useRegisterClassListInfinityQuery,
  useRegisterClassListQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { IssuerAddClassModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddClassModal.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { goodFormatNumber } from 'ui/utils';

type BaseProps = {
  label?: string | ReactNode;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  withBorder?: boolean;
  registerId?: number;
  showAddNewItem?: boolean;
  placeholder?: string;
  showBorderBottom?: boolean;
  classNameSelect?: string;
  showPriceInLabel?: boolean;
};

export interface ClassItemOption {
  id: number;
  class_name: string;
  register_id: number;
  current_price_per_share: number;
  min_investment: number;
  transfer_lock_period?: number;
  toString: () => string;
}

type NumOption = SelectPaginateOption<ClassItemOption>;

/* ---------- Single-select ---------- */

// clearable: false | undefined
type SingleNonClearableProps = BaseProps & {
  isMulti?: false | undefined;
  clearable?: false | undefined;
  onChange: (opt: NumOption, fieldName: string) => void;
  defaultValue?: NumOption;
  value?: number | null;
};

// clearable: true
type SingleClearableProps = BaseProps & {
  isMulti?: false | undefined;
  clearable: true;
  onChange: (opt: NumOption | null, fieldName: string) => void;
  defaultValue?: NumOption | null;
  value?: number | null;
};

/* ---------- Multi-select ---------- */

// clearable: false | undefined
type MultiNonClearableProps = BaseProps & {
  isMulti: true;
  clearable?: false | undefined;
  onChange: (opt: NumOption[], fieldName: string) => void;
  defaultValue?: NumOption[];
  value?: number[] | null;
};

// clearable: true
type MultiClearableProps = BaseProps & {
  isMulti: true;
  clearable: true;
  onChange: (opt: NumOption[] | null, fieldName: string) => void;
  defaultValue?: NumOption[] | null;
  value?: number[] | null;
};

export type RegisterClassesSelectPaginateProps =
  | SingleNonClearableProps
  | SingleClearableProps
  | MultiNonClearableProps
  | MultiClearableProps;

const RegisterClassesSelectPaginate = ({
  error,
  label,
  isMulti = false,
  disabled,
  clearable = false,
  className,
  withBorder = false,
  defaultValue,
  value,
  formFieldTitle = '',
  onChange,
  registerId,
  showAddNewItem = false,
  placeholder,
  showBorderBottom,
  classNameSelect,
  showPriceInLabel = true,
}: RegisterClassesSelectPaginateProps) => {
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [rand, setRand] = useState(1);
  const [params] = useSearchParams();
  const getRegisterTypeString = useRegisterTypeStrings();
  const register_Id = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const { fetchNextPage } = useRegisterClassListInfinityQuery(
    { page: 1, page_size: 10, register_id: registerId || register_Id },
    true,
  );

  const { data: dataClasses, isFetching } = useRegisterClassListQuery({
    page: 1,
    page_size: 50,
    register_id: registerId || +register_Id,
  });

  const valueWithLabel = useMemo<
    | SelectPaginateOption<ClassItemOption>
    | SelectPaginateOption<ClassItemOption>[]
    | null
    | undefined
  >(() => {
    if (!value) return null;

    const allClasses = (dataClasses?.data || [])?.map<SelectPaginateOption<ClassItemOption>>(
      ({
        id,
        class_name,
        register_id,
        current_price_per_share,
        min_investment,
        transfer_lock_period,
      }) => ({
        label: (
          <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
            <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
              <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                <span
                  className={classNames(
                    'text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis',
                  )}
                >
                  {class_name}
                </span>
              </span>
            </span>

            {showPriceInLabel && (
              <span className="text-gray-600 text-xs">
                {getRegisterTypeString('nav')}: ${goodFormatNumber(current_price_per_share || 0)}
              </span>
            )}
          </span>
        ),
        value: {
          id,
          class_name,
          register_id,
          current_price_per_share,
          min_investment,
          transfer_lock_period,
          toString: () => String(id),
        },
      }),
    );
    const options =
      isMulti && isArray(value)
        ? allClasses.filter(option => value?.includes(option.value?.id))
        : allClasses.find(option => option.value?.id === value);
    return options;
  }, [dataClasses?.data, isMulti, value, showPriceInLabel]);

  const handleAsyncLoadFormOptions = useCallback(
    async (page: number, search?: string) => {
      const classesData = await fetchNextPage({ pageParam: { page, search } });

      const classes = classesData?.data?.pages?.at(-1)?.data || [];
      const items = classes.map<SelectPaginateOption<ClassItemOption>>(
        ({
          id,
          class_name,
          register_id,
          current_price_per_share,
          min_investment,
          transfer_lock_period,
        }) => ({
          label: (
            <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
              <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                  <span
                    className={classNames(
                      'text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis',
                    )}
                  >
                    {class_name}
                  </span>
                </span>
              </span>

              {showPriceInLabel && (
                <span className="text-gray-600 text-xs">
                  {getRegisterTypeString('nav')}: ${goodFormatNumber(current_price_per_share || 0)}
                </span>
              )}
            </span>
          ),
          value: {
            id,
            class_name,
            register_id,
            current_price_per_share,
            min_investment,
            transfer_lock_period,
            toString: () => String(id),
          },
        }),
      );

      return items;
    },
    [fetchNextPage, getRegisterTypeString, showPriceInLabel],
  );

  const handleOnSuccessAddedClass = () => {
    setRand(random(true) + 1000);
  };

  return (
    <>
      <SelectPaginate<ClassItemOption, boolean>
        cacheUniqs={[register_Id, registerId, `class_${rand}`]}
        isClearable={clearable}
        isLoading={isFetching}
        disabled={disabled}
        isMulti={isMulti}
        placeholder={placeholder}
        label={label}
        className={className}
        classNameSelect={classNameSelect}
        defaultValue={defaultValue}
        value={valueWithLabel}
        onAsync={handleAsyncLoadFormOptions}
        onChange={options => onChange(options as any, formFieldTitle)}
        error={error}
        debounceTimeoutSearch={700}
        withBorder={withBorder}
        showBorderBottom={showBorderBottom}
        addedLastOption={
          showAddNewItem
            ? {
                label: 'Add new class',
                onClick: () => {
                  setIsOpenAdd(true);
                },
              }
            : undefined
        }
      />
      <IssuerAddClassModal
        onClose={() => setIsOpenAdd(false)}
        open={isOpenAdd}
        onSuccess={handleOnSuccessAddedClass}
      />
    </>
  );
};

export default RegisterClassesSelectPaginate;
